import React, { useMemo } from 'react';
import { User } from '../../types/user';
import { useOrganizationContext } from '../../context/OrganizationContext';
import useSWR from 'swr';
import { useClient } from '../../hooks/useClient';
import './CompactEmployeeSelect.scss';
import { AdaptiveSelect } from '../AdaptiveSelect/AdaptiveSelect';

interface Props {
  value: User[];
  onChange: (employees: User[]) => void;
  className?: string;
}

interface EmployeeOption extends Partial<User> {
  label: string;
  value: string;
}

// Employee filter of the reservations calendar: several employees can be chosen and the trigger shows none of them (the filter
// writes its own label next to it).
export const CompactEmployeeSelect = ({ value, onChange, className }: Props) => {
  const { get } = useClient();
  const organization = useOrganizationContext()?.organization;

  const { data: employees, isLoading: employeesLoading } = useSWR(
    organization ? `/organizations/${organization.id}/users` : null,
    get<User[]>,
  );

  const employeeOptions = useMemo(() => {
    return (
      employees?.map((employee) => ({
        ...employee,
        label: `${employee.name} ${employee.surnames}`,
        value: employee.id,
      })) || []
    );
  }, [employees]);

  const itemTemplate = (option: EmployeeOption) => (
    <div className='compact-employee-item'>
      <div className='compact-employee-avatar'>
        {option.avatarUrl ? <img src={option.avatarUrl} alt={option.label} /> : <span>{option.label.charAt(0)}</span>}
      </div>
      <span className='compact-employee-name'>{option.label}</span>
    </div>
  );

  const handleChange = (selectedIds: string[]) => {
    const selectedEmployees = selectedIds
      .map((id) => employees?.find((employee) => employee.id === id))
      .filter((employee): employee is User => !!employee);
    onChange(selectedEmployees);
  };

  const convertedValue = useMemo(() => value.map((employee) => employee.id), [value]);

  return (
    <AdaptiveSelect
      value={convertedValue}
      onChange={handleChange}
      options={employeeOptions}
      className={className}
      itemTemplate={itemTemplate}
      selectedItemTemplate={() => null}
      loading={employeesLoading}
      isMultiple
      emptyFilterMessage='No se encontraron empleados'
      placeholder='Buscar empleado...'
      modalTitle='Seleccionar empleado'
      searchPlaceholder='Buscar empleado'
      isFilter
    />
  );
};
